/**
 * Korrespondenz (technisch: Schreibstube; Pilot 11.10.2026, Arbeitsblock 12.10.2026, vereinheitlicht 25.10.2026) – der EINE Arbeitsplatz
 * für ausgehende Schreiben. Einstiege: Menü „Korrespondenz“, „Brief erstellen“ (Akte/Aufgabe), Entwurf aus Unteraufgaben, ZEO, alte Links.
 * Anlagen nur aus der Akte des Klienten; Versandwege nur so, wie sie hier tatsächlich funktionieren (/versandwege); Altbestand nur lesend.
 * Reihenfolge: FÜR · IN DER SACHE · BEZÜGLICH · AN · VERSANDWEG · ABSENDER · AUFGABE · IST ANTWORT AUF · BETREFF · TEXT.
 * AN: Vorschlag aus den Aktenbeziehungen ODER freie Eingabe (speichern in der Akte nur nach Vorschau + Bestätigung).
 * ABSENDER: Standard = zuständiger Betreuer, auswählbar; "Erfasst von" (Anmeldung) bleibt getrennt im Audit.
 * Live-PDF-Vorschau ohne Speichern. Speichern = derselbe Datensatz. Versenden immer erst nach finaler Vorschau + Bestätigung.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, BookmarkPlus, CheckCircle2, ChevronLeft, ChevronRight, ClipboardList, Download, FileText, History, Library, Loader2, Mail, Paperclip, Plus,
  Printer, Save, Send, Sparkles, Upload, Wand2, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useTabSearchParams } from '@/hooks/useTabRoute';
import { schreibstubeAPI } from '@/api/schreibstubeAPI';
import PruefAnsicht from './PruefAnsicht';
import AufgabenBezug from './AufgabenBezug';
import {
  SPAETERE_VERSANDWEGE, STATUS_LABEL, VERSANDWEGE, alsFreierEmpfaenger, ausZeoVorschlag, begrenze, bezugOptionen, entwurfBody, fuegeEin,
  istGeaendert, leererEntwurf, mitEmpfaenger, offenePlatzhalter, refSchluessel, setzeAnschrift, versandMaengel, vorschauSchluessel,
  GRUSSFORMEL, anredeAusTextLoesen, aufbauMaengel, autoAnrede, mitAutoAnrede,
  aufloesungFuer, empfaengerFuerSachgebiet, empfaengerPasstNicht, mitSachgebiet, refGleich, mitAnlage, ohneAnlage,
  anlageVorpruefung, emailGueltig, faxGueltig, filtereBausteine, hauptaufgaben, markierterText, mitFreiemNamen, mitOrtVorschlag, nameUndEmail,
  neuerToken, plzGueltig, speicherZustand, ANLAGE_ENDUNGEN,
} from './schreibstube';

const SEL = 'w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm disabled:bg-slate-50';
const SPALTEN_SCHLUESSEL = 'schreibstube_spalten_v1';

function Zeile({ label, children, hinweis, testid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[9rem_1fr] gap-x-3 gap-y-1 items-start py-1.5 border-b border-slate-100 last:border-0" data-testid={testid}>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:pt-2.5">{label}</span>
      <div className="min-w-0">
        {children}
        {hinweis && <span className="block text-xs text-slate-500 mt-1">{hinweis}</span>}
      </div>
    </div>
  );
}

/** Beschriftetes Eingabefeld (Bezeichnung sichtbar, nicht nur als Platzhalter) mit sofortiger Fehlermeldung. */
function Feld({ label, id, className = '', fehler, hinweis, children }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <label htmlFor={id} className="block text-[11px] font-medium text-slate-600 mb-0.5">{label}</label>
      {children}
      {fehler ? <span className="block text-xs text-red-600 mt-0.5" data-testid={`${id}-fehler`}>{fehler}</span>
        : hinweis ? <span className="block text-xs text-slate-500 mt-0.5" data-testid={`${id}-hinweis`}>{hinweis}</span> : null}
    </div>
  );
}

function leseSpalten() {
  try { return JSON.parse(window.localStorage.getItem(SPALTEN_SCHLUESSEL) || 'null') || {}; } catch { return {}; }
}

function merkeSpalten(w) {
  try { window.localStorage.setItem(SPALTEN_SCHLUESSEL, JSON.stringify(w)); } catch { /* nur Komfort */ }
}

/** Ziehgriff zwischen zwei Spalten (nur Desktop). onZiehen(dx) erhaelt die Mausbewegung seit Beginn. */
function Ziehgriff({ onStart, onZiehen, onEnde, testid }) {
  const start = (ev) => {
    ev.preventDefault();
    const x0 = ev.clientX;
    onStart();
    // Abdeckung waehrend des Ziehens: sonst schluckt die PDF-Vorschau (iframe) Mausbewegung und Loslassen
    const abdeckung = document.createElement('div');
    abdeckung.setAttribute('data-testid', 'sst-zieh-abdeckung');
    abdeckung.style.cssText = 'position:fixed;inset:0;z-index:9999;cursor:col-resize;';
    document.body.appendChild(abdeckung);
    const bewege = (e) => onZiehen(e.clientX - x0);
    const los = () => {
      window.removeEventListener('mousemove', bewege); window.removeEventListener('mouseup', los); abdeckung.remove(); onEnde();
    };
    window.addEventListener('mousemove', bewege);
    window.addEventListener('mouseup', los);
  };
  return (
    <div role="separator" aria-orientation="vertical" title="Spaltenbreite ziehen" onMouseDown={start} data-testid={testid}
      className="hidden lg:flex w-2 shrink-0 cursor-col-resize items-center justify-center group">
      <div className="w-0.5 h-16 rounded bg-slate-300 group-hover:bg-violet-500" />
    </div>
  );
}

function KlientWahl({ klient, onWaehlen, sperrHinweis }) {
  const [q, setQ] = useState('');
  const [offen, setOffen] = useState(false);
  const [treffer, setTreffer] = useState([]);
  const [laedt, setLaedt] = useState(false);
  const [aktiv, setAktiv] = useState(0);
  useEffect(() => {
    if (!offen) return undefined;
    setLaedt(true);
    const t = setTimeout(() => {
      schreibstubeAPI.klienten(q.trim()).then((r) => { setTreffer(r.klienten || []); setAktiv(0); })
        .catch(() => setTreffer([])).finally(() => setLaedt(false));
    }, q.trim() ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, offen]);
  const waehle = (k) => { setOffen(false); setQ(''); onWaehlen(k); };
  const taste = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOffen(true); setAktiv((a) => Math.min(a + 1, treffer.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAktiv((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && offen && treffer[aktiv]) { e.preventDefault(); waehle(treffer[aktiv]); }
    else if (e.key === 'Escape') setOffen(false);
  };
  return (
    <div className="relative">
      {klient && <div className="text-sm font-semibold text-slate-800 mb-1" data-testid="sst-klient-name">{klient.name}</div>}
      <Input placeholder={klient ? 'Anderen Klienten wählen – Name oder Aktenzeichen …' : 'Klient wählen – anklicken oder Name/Aktenzeichen tippen …'}
        value={q} onChange={(e) => { setQ(e.target.value); setOffen(true); }} onFocus={() => setOffen(true)} onClick={() => setOffen(true)}
        onBlur={() => setTimeout(() => setOffen(false), 150)} onKeyDown={taste} className="h-9" data-testid="sst-klient-suche" autoComplete="off" />
      {sperrHinweis && klient && <span className="block text-xs text-slate-400 mt-1">{sperrHinweis}</span>}
      {offen && (
        <div className="absolute z-30 left-0 right-0 mt-1 border rounded-md bg-white shadow-lg max-h-80 overflow-y-auto" data-testid="sst-klient-liste">
          {laedt && treffer.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Lädt …</div>}
          {!laedt && treffer.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Kein Klient gefunden.</div>}
          {treffer.map((k, i) => (
            <button key={k.id} type="button" data-testid="sst-klient-treffer"
              className={`block w-full text-left px-3 py-1.5 text-sm ${i === aktiv ? 'bg-violet-50' : 'hover:bg-violet-50'}`}
              onMouseDown={(e) => { e.preventDefault(); waehle(k); }} onMouseEnter={() => setAktiv(i)}>
              <span className="font-medium">{k.name}</span>
              {k.geburtsdatum ? <span className="text-slate-400"> · {k.geburtsdatum}</span> : null}
              {k.aktenzeichen ? <span className="text-slate-400"> · {k.aktenzeichen}</span> : null}
              {k.status && k.status !== 'aktiv' ? <span className="ml-1 text-[10px] uppercase text-amber-700">{k.status}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AnBlock({ entwurf, setEntwurf, empfaenger, sachgebiete, kontext, gesperrt, onKontaktSpeichern }) {
  const [frei, setFrei] = useState(() => !entwurf.empfaenger_ref && !!entwurf.empfaenger_name);
  const [alle, setAlle] = useState(false);
  const [behalten, setBehalten] = useState('');
  useEffect(() => { if (entwurf.empfaenger_ref) setFrei(false); }, [entwurf.empfaenger_ref]);
  // neues Sachgebiet: wieder nur die fachlich passenden Empfaenger zeigen; ein leerer AN fuehrt zur Akte zurueck
  useEffect(() => { setAlle(false); }, [entwurf.sachgebiet]);
  const setze = (feld, wert) => setEntwurf((e) => ({ ...e, [feld]: wert }));
  const a = entwurf.empfaenger_anschrift || {};
  const zeigeAltAdresse = frei && !entwurf.empfaenger_anschrift && (entwurf.empfaenger_adresse || '').trim();
  const sg = (sachgebiete || []).find((s) => s.key === entwurf.sachgebiet);
  const aufl = aufloesungFuer(sachgebiete, entwurf.sachgebiet);
  const liste = alle ? empfaenger : empfaengerFuerSachgebiet(empfaenger, aufl ? entwurf.sachgebiet : null, entwurf.empfaenger_ref);
  const istAuto = aufl && aufl.status === 'eindeutig' && refGleich(aufl.ref, entwurf.empfaenger_ref);
  const passtNicht = empfaengerPasstNicht(entwurf, kontext) && behalten !== `${entwurf.sachgebiet}|${refSchluessel(entwurf.empfaenger_ref)}`;
  const passender = aufl && aufl.status === 'eindeutig' ? empfaenger.find((x) => refGleich(x.ref, aufl.ref)) : null;
  // Freie Eingabe: Orte zur PLZ aus Anschriften, die Olympus bereits kennt (keine externe API); manuelle Eingabe bleibt moeglich
  const [orte, setOrte] = useState([]);
  const plz = (a.plz || '').trim();
  useEffect(() => {
    if (!frei || !plzGueltig(plz)) { setOrte([]); return undefined; }
    let aktiv = true;
    schreibstubeAPI.plzOrte(plz).then((r) => {
      if (!aktiv) return;
      setOrte(r.orte || []);
      setEntwurf((x) => mitOrtVorschlag(x, plz, r.orte || []));
    }).catch(() => { if (aktiv) setOrte([]); });
    return () => { aktiv = false; };
  }, [frei, plz, setEntwurf]);
  const email = (entwurf.empfaenger_email || '').trim();
  const emailFehler = email && !emailGueltig(email) ? 'Keine gültige E-Mail-Adresse (Beispiel: name@beispiel.de).'
    : !email && entwurf.versandweg === 'email' ? 'Für den Versand per E-Mail bitte eine E-Mail-Adresse eintragen.' : null;
  const emailAusName = !!email && email === nameUndEmail(entwurf.empfaenger_name)[1];
  return (
    <div className="space-y-2" data-testid="sst-an">
      <div className="flex gap-1.5">
        <button type="button" disabled={gesperrt} onClick={() => setFrei(false)} data-testid="sst-an-akte"
          className={`h-8 px-3 rounded-md border text-xs ${!frei ? 'bg-violet-600 text-white border-violet-600' : 'bg-white hover:bg-violet-50'}`}>Aus der Akte</button>
        <button type="button" disabled={gesperrt} onClick={() => { setFrei(true); setEntwurf((e) => alsFreierEmpfaenger(e)); }} data-testid="sst-an-frei"
          className={`h-8 px-3 rounded-md border text-xs ${frei ? 'bg-violet-600 text-white border-violet-600' : 'bg-white hover:bg-violet-50'}`}>Freie Eingabe</button>
      </div>
      {!frei ? (
        <>
          <select className={SEL} disabled={!kontext || gesperrt} value={refSchluessel(entwurf.empfaenger_ref)} data-testid="sst-empfaenger"
            onChange={(e) => setEntwurf((alt) => mitEmpfaenger(alt, empfaenger.find((x) => refSchluessel(x.ref) === e.target.value), sachgebiete))}>
            <option value="">— Empfänger aus den Beziehungen wählen —</option>
            {liste.map((x) => <option key={refSchluessel(x.ref)} value={refSchluessel(x.ref)}>{x.name}{x.beziehung || x.art ? ` (${x.beziehung || x.art})` : ''}</option>)}
          </select>
          {kontext && empfaenger.length === 0 && <div className="text-xs text-slate-500">Für diesen Klienten sind keine Personen/Institutionen verknüpft – „Freie Eingabe“ nutzen.</div>}
          {istAuto && (
            <div className="text-xs text-green-700" data-testid="sst-an-auto">
              Automatisch aus der Akte ({aufl.quelle === 'beziehung' ? 'Beziehung des Klienten' : aufl.quelle.startsWith('name:') ? 'Klientenangabe, exakter Namenstreffer' : `Akte: ${aufl.quelle}`}) – änderbar.
            </div>)}
          {aufl && aufl.status === 'mehrere' && !entwurf.empfaenger_ref && (
            <div className="text-xs text-amber-700" data-testid="sst-an-mehrere">Mehrere passende Empfänger zu „{sg.label}“ – bitte auswählen.</div>)}
          {aufl && aufl.status === 'keiner' && !entwurf.empfaenger_ref && (
            <div className="text-xs bg-amber-50 text-amber-800 rounded p-2 space-y-1.5" data-testid="sst-an-keiner">
              <div>Für diesen Klienten ist zu „{sg.label}“ kein eindeutiger Empfänger in der Akte hinterlegt.</div>
              <Button type="button" size="sm" variant="outline" disabled={gesperrt} data-testid="sst-an-keiner-frei"
                onClick={() => { setFrei(true); setEntwurf((e) => alsFreierEmpfaenger(e)); }}>Empfänger frei eingeben</Button>
            </div>)}
          {passtNicht && (
            <div className="text-xs bg-amber-50 text-amber-800 rounded p-2 space-y-1.5" data-testid="sst-an-passt-nicht">
              <div>Der gewählte Empfänger passt fachlich nicht zu „{sg ? sg.label : entwurf.sachgebiet}“. Bitte entscheiden:</div>
              <div className="flex flex-wrap gap-1.5">
                <Button type="button" size="sm" variant="outline" disabled={gesperrt} data-testid="sst-an-behalten"
                  onClick={() => setBehalten(`${entwurf.sachgebiet}|${refSchluessel(entwurf.empfaenger_ref)}`)}>Behalten</Button>
                {passender && (
                  <Button type="button" size="sm" variant="outline" disabled={gesperrt} data-testid="sst-an-passenden"
                    onClick={() => setEntwurf((e) => mitEmpfaenger(e, passender, sachgebiete))}>{passender.name} übernehmen</Button>)}
                <Button type="button" size="sm" variant="outline" disabled={gesperrt} data-testid="sst-an-leeren"
                  onClick={() => setEntwurf((e) => mitEmpfaenger(e, null, sachgebiete))}>Empfänger entfernen</Button>
              </div>
            </div>)}
          {aufl && empfaenger.length > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-slate-500">
              <input type="checkbox" checked={alle} disabled={gesperrt} onChange={(ev) => setAlle(ev.target.checked)} data-testid="sst-an-alle" />
              alle Beziehungen des Klienten anzeigen (auch fachfremde)
            </label>)}
          {!entwurf.empfaenger_ref && entwurf.empfaenger_name && (
            <div className="text-xs text-amber-700">Bisher: „{entwurf.empfaenger_name}“ – aus den Beziehungen zuordnen oder „Freie Eingabe“ wählen.</div>)}
          {entwurf.empfaenger_ref && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input placeholder="Ansprechpartner (optional)" value={entwurf.empfaenger_ansprechpartner || ''} disabled={gesperrt} className="h-9"
                onChange={(e) => setze('empfaenger_ansprechpartner', e.target.value)} data-testid="sst-ansprechpartner" />
              <Input placeholder="Telefon" value={entwurf.empfaenger_telefon || ''} disabled={gesperrt} className="h-9"
                onChange={(e) => setze('empfaenger_telefon', e.target.value)} data-testid="sst-telefon" />
              <Textarea rows={2} placeholder={'Anschrift (Straße Nr.\nPLZ Ort)'} value={entwurf.empfaenger_adresse || ''} disabled={gesperrt} className="md:col-span-2"
                onChange={(e) => setze('empfaenger_adresse', e.target.value)} data-testid="sst-adresse" />
              <div>
                <Input placeholder="E-Mail-Adresse" value={entwurf.empfaenger_email || ''} disabled={gesperrt} className={`h-9 ${email && !emailGueltig(email) ? 'border-red-400' : ''}`}
                  aria-invalid={!!(email && !emailGueltig(email))} onChange={(e) => setze('empfaenger_email', e.target.value)} data-testid="sst-email" />
                {email && !emailGueltig(email) && <span className="block text-xs text-red-600 mt-0.5" data-testid="sst-email-fehler">Keine gültige E-Mail-Adresse.</span>}
              </div>
              <Input placeholder="Fax" value={entwurf.empfaenger_fax || ''} disabled={gesperrt} className="h-9"
                onChange={(e) => setze('empfaenger_fax', e.target.value)} data-testid="sst-fax" />
              {!entwurf.empfaenger_adresse && <span className="text-xs text-slate-500 md:col-span-2">Keine Anschrift in der Akte hinterlegt – für einen Brief bitte eintragen.</span>}
            </div>
          )}
        </>
      ) : (
        <div className="grid grid-cols-6 gap-x-2 gap-y-1.5" data-testid="sst-frei">
          <Feld label="Empfänger (Name oder Institution)" className="col-span-6" id="sst-frei-name">
            <Input id="sst-frei-name" placeholder="z. B. Landratsamt Augsburg" value={entwurf.empfaenger_name || ''} disabled={gesperrt} className="h-9"
              onChange={(e) => setEntwurf((x) => mitFreiemNamen(x, e.target.value))} data-testid="sst-frei-name" />
          </Feld>
          <Feld label="E-Mail-Adresse" className="col-span-6" id="sst-frei-email"
            fehler={emailFehler} hinweis={emailAusName ? 'Aus dem Feld „Empfänger“ übernommen – hier änderbar.' : null}>
            <Input id="sst-frei-email" type="email" placeholder="name@beispiel.de" value={entwurf.empfaenger_email || ''} disabled={gesperrt}
              className={`h-9 ${emailFehler ? 'border-red-400' : ''}`} aria-invalid={!!emailFehler}
              onChange={(e) => setze('empfaenger_email', e.target.value)} data-testid="sst-frei-email" />
          </Feld>
          <Feld label="Ansprechpartner (optional)" className="col-span-6" id="sst-frei-ansprechpartner">
            <Input id="sst-frei-ansprechpartner" value={entwurf.empfaenger_ansprechpartner || ''} disabled={gesperrt} className="h-9"
              onChange={(e) => setze('empfaenger_ansprechpartner', e.target.value)} data-testid="sst-frei-ansprechpartner" />
          </Feld>
          <Feld label="Straße" className="col-span-4" id="sst-frei-strasse">
            <Input id="sst-frei-strasse" value={a.strasse || ''} disabled={gesperrt} className="h-9"
              onChange={(e) => setEntwurf((x) => setzeAnschrift(x, 'strasse', e.target.value))} data-testid="sst-frei-strasse" />
          </Feld>
          <Feld label="Hausnr." className="col-span-2" id="sst-frei-hausnummer">
            <Input id="sst-frei-hausnummer" value={a.hausnummer || ''} disabled={gesperrt} className="h-9"
              onChange={(e) => setEntwurf((x) => setzeAnschrift(x, 'hausnummer', e.target.value))} data-testid="sst-frei-hausnummer" />
          </Feld>
          <Feld label="PLZ" className="col-span-2" id="sst-frei-plz" fehler={(a.plz || '').trim() && !plzGueltig(a.plz) ? '5 Ziffern' : null}>
            <Input id="sst-frei-plz" inputMode="numeric" maxLength={5} value={a.plz || ''} disabled={gesperrt} className="h-9"
              onChange={(e) => setEntwurf((x) => setzeAnschrift(x, 'plz', e.target.value.replace(/\s/g, '')))} data-testid="sst-frei-plz" />
          </Feld>
          <Feld label="Ort" className="col-span-4" id="sst-frei-ort">
            <Input id="sst-frei-ort" list="sst-frei-orte" value={a.ort || ''} disabled={gesperrt} className="h-9" autoComplete="off"
              onChange={(e) => setEntwurf((x) => setzeAnschrift(x, 'ort', e.target.value))} data-testid="sst-frei-ort" />
            <datalist id="sst-frei-orte">{orte.map((o) => <option key={o} value={o} />)}</datalist>
            {orte.length > 1 && !gesperrt && (
              <div className="flex flex-wrap gap-1 mt-1" data-testid="sst-frei-orte">
                <span className="text-xs text-slate-500">Orte zur PLZ:</span>
                {orte.map((o) => (
                  <button key={o} type="button" data-testid="sst-frei-ort-vorschlag" onClick={() => setEntwurf((x) => setzeAnschrift(x, 'ort', o))}
                    className={`text-xs px-1.5 rounded border ${a.ort === o ? 'bg-violet-600 text-white border-violet-600' : 'bg-white hover:bg-violet-50'}`}>{o}</button>))}
              </div>)}
          </Feld>
          <Feld label="Telefon" className="col-span-3" id="sst-frei-telefon">
            <Input id="sst-frei-telefon" value={entwurf.empfaenger_telefon || ''} disabled={gesperrt} className="h-9"
              onChange={(e) => setze('empfaenger_telefon', e.target.value)} data-testid="sst-frei-telefon" />
          </Feld>
          <Feld label="Fax" className="col-span-3" id="sst-frei-fax" fehler={(entwurf.empfaenger_fax || '').trim() && !faxGueltig(entwurf.empfaenger_fax) ? 'Keine gültige Faxnummer' : null}>
            <Input id="sst-frei-fax" value={entwurf.empfaenger_fax || ''} disabled={gesperrt} className="h-9"
              onChange={(e) => setze('empfaenger_fax', e.target.value)} data-testid="sst-frei-fax" />
          </Feld>
          {zeigeAltAdresse && (
            <div className="col-span-6 text-xs text-slate-500">Bisherige Anschrift: <span className="whitespace-pre-line">{entwurf.empfaenger_adresse}</span> – wird verwendet, solange oben nichts eingetragen ist.</div>)}
          <div className="col-span-6 flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" disabled={gesperrt || !(entwurf.empfaenger_name || '').trim()} onClick={onKontaktSpeichern} data-testid="sst-kontakt-speichern">
              <BookmarkPlus className="w-4 h-4 mr-1" /> Als Kontakt in der Akte speichern …
            </Button>
            <span className="text-xs text-slate-400">optional – erst nach Vorschau und Bestätigung</span>
          </div>
        </div>
      )}
    </div>
  );
}

function KontaktDialog({ entwurf, onAbbrechen, onGespeichert }) {
  const [art, setArt] = useState('institution');
  const [vs, setVs] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const daten = useMemo(() => ({ klient_id: entwurf.klient_id, art, name: entwurf.empfaenger_name || '', anschrift: entwurf.empfaenger_anschrift,
    telefon: entwurf.empfaenger_telefon || '', fax: entwurf.empfaenger_fax || '', email: entwurf.empfaenger_email || '' }), [entwurf, art]);
  useEffect(() => {
    setVs(null); setFehler(null);
    schreibstubeAPI.kontaktVorschau(daten).then(setVs).catch((e) => setFehler(e.message));
  }, [daten]);
  const speichern = async () => {
    setLaeuft(true); setFehler(null);
    try {
      const r = await schreibstubeAPI.kontaktSpeichern({ ...daten, bestaetigt: true });
      onGespeichert(r);
    } catch (e) { setFehler(e.message); setLaeuft(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="sst-kontakt-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-3 text-sm">
        <div className="flex items-center justify-between"><h2 className="font-semibold">Kontakt in der Akte speichern</h2>
          <button type="button" onClick={onAbbrechen} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
        <div className="flex gap-3">
          {[['institution', 'Institution'], ['person', 'Person']].map(([k, l]) => (
            <label key={k} className="flex items-center gap-1"><input type="radio" checked={art === k} onChange={() => setArt(k)} data-testid={`sst-kontakt-art-${k}`} /> {l}</label>))}
        </div>
        <div className="rounded bg-slate-50 p-3 space-y-1 text-slate-700">
          <div><strong>{daten.name}</strong></div>
          {[daten.anschrift && [daten.anschrift.strasse, daten.anschrift.hausnummer].filter(Boolean).join(' '),
            daten.anschrift && [daten.anschrift.plz, daten.anschrift.ort].filter(Boolean).join(' '),
            daten.telefon && `Tel. ${daten.telefon}`, daten.fax && `Fax ${daten.fax}`, daten.email].filter(Boolean).map((z) => <div key={z}>{z}</div>)}
        </div>
        {!vs && !fehler && <Loader2 className="animate-spin text-slate-400" />}
        {vs && <div className="text-slate-700" data-testid="sst-kontakt-vorschau">{vs.text}</div>}
        {fehler && <div className="text-red-600" data-testid="sst-kontakt-fehler">{fehler}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onAbbrechen}>Abbrechen</Button>
          <Button onClick={speichern} disabled={!vs || laeuft} data-testid="sst-kontakt-bestaetigen">
            {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : vs && vs.aktion === 'bereits_verknuepft' ? 'Als Empfänger übernehmen' : 'Bestätigen und speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ZeoDialog({ entwurf, kontext, onAbbrechen, onUebernehmen }) {
  const [stichworte, setStichworte] = useState('');
  const [ueberarbeiten, setUeberarbeiten] = useState(false);
  const [vorschlag, setVorschlag] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  const los = async () => {
    setLaeuft(true); setFehler(null); setVorschlag(null);
    try {
      setVorschlag(await schreibstubeAPI.formulieren({ klient_id: entwurf.klient_id, empfaenger_name: entwurf.empfaenger_name || '', sachgebiet: entwurf.sachgebiet,
        bezug: entwurf.bezug, betreff: entwurf.betreff || '', stichworte, bisheriger_text: ueberarbeiten ? entwurf.inhalt || '' : '' }));
    } catch (e) { setFehler(e.message); }
    setLaeuft(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="sst-zeo-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col text-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b"><h2 className="font-semibold flex items-center gap-1.5"><Sparkles size={16} /> Mit ZEO formulieren</h2>
          <button type="button" onClick={onAbbrechen} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="text-xs text-slate-500">Für {kontext ? kontext.klient.name : ''}{entwurf.empfaenger_name ? ` an ${entwurf.empfaenger_name}` : ''}. ZEO verwendet nur Angaben aus der Akte und Ihre Stichworte; es wird nichts gespeichert.</div>
          <Textarea rows={4} placeholder="Stichworte zum Anliegen, z. B.: Mitgliedsbescheinigung anfordern, Frist 2 Wochen" value={stichworte}
            onChange={(e) => setStichworte(e.target.value)} data-testid="sst-zeo-stichworte" />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={ueberarbeiten} onChange={(e) => setUeberarbeiten(e.target.checked)} data-testid="sst-zeo-ueberarbeiten" /> Bisherigen Text sprachlich überarbeiten</label>
          <Button onClick={los} disabled={laeuft || (!stichworte.trim() && !ueberarbeiten)} data-testid="sst-zeo-los">
            {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1" /> Vorschlag erzeugen</>}</Button>
          {fehler && <div className="text-red-600" data-testid="sst-zeo-fehler">{fehler}</div>}
          {vorschlag && (
            <div className="space-y-2">
              {vorschlag.hinweise.map((h) => <div key={h} className="text-xs text-amber-700 bg-amber-50 rounded p-2">{h}</div>)}
              <pre className="whitespace-pre-wrap font-sans bg-slate-50 rounded p-3 border" data-testid="sst-zeo-vorschlag">{vorschlag.inhalt}</pre>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onAbbrechen}>Verwerfen</Button>
          <Button variant="outline" disabled={!vorschlag} onClick={() => onUebernehmen(vorschlag.inhalt, 'anfuegen')} data-testid="sst-zeo-anfuegen">Unten anfügen</Button>
          <Button disabled={!vorschlag} onClick={() => onUebernehmen(vorschlag.inhalt, 'ersetzen')} data-testid="sst-zeo-ersetzen">Text ersetzen</Button>
        </div>
      </div>
    </div>
  );
}

const KANAL_TEXT = {
  email: { knopf: 'Jetzt per E-Mail senden', bestaetigung: 'Ich habe Empfänger, Betreff, Text und PDF geprüft. Jetzt genau diese Fassung per E-Mail senden.' },
  fax: { knopf: 'Jetzt faxen', bestaetigung: 'Ich habe Faxnummer, Text und PDF geprüft. Jetzt genau diese Fassung als Fax senden.' },
  post: { knopf: 'Als per Post versendet markieren', bestaetigung: 'Ich habe genau diese Fassung ausgedruckt und per Post versendet.' },
};

function VersandDialog({ entwurf, onAbbrechen, onVersendet }) {
  const [vv, setVv] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [bestaetigt, setBestaetigt] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  useEffect(() => {
    let url = null;
    schreibstubeAPI.versandvorschau(entwurf.id).then(setVv).catch((e) => setFehler(e.message));
    schreibstubeAPI.entwurfPdf(entwurf.id).then((b) => { url = URL.createObjectURL(b); setPdfUrl(url); }).catch((e) => setFehler(e.message));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [entwurf.id, entwurf.version]);
  const kanal = vv ? vv.kanal : null;
  const technisch = kanal === 'email' || kanal === 'fax';
  const gesperrt = technisch && vv.email_freigegeben === false;
  const ausfuehren = async () => {
    if (laeuft || !vv || !bestaetigt) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const body = { version: vv.version, inhalt_hash: vv.inhalt_hash, bestaetigt: true };
      const r = kanal === 'email' ? await schreibstubeAPI.senden(entwurf.id, body)
        : kanal === 'fax' ? await schreibstubeAPI.faxen(entwurf.id, body) : await schreibstubeAPI.alsPostVersendet(entwurf.id, body);
      toast.success(r.bereits_versendet ? 'Dieses Schreiben war bereits versendet – nichts erneut gesendet.'
        : kanal === 'email' ? 'E-Mail versendet.' : kanal === 'fax' ? 'Fax versendet.' : 'Als per Post versendet markiert.');
      onVersendet(r);
    } catch (e) {
      setFehler(e.message);
      setLaeuft(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="sst-versand-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="font-semibold text-slate-800">Finale Prüfung vor dem Versand</h2>
          <button type="button" onClick={onAbbrechen} className="text-slate-400 hover:text-slate-700" data-testid="sst-versand-abbrechen"><X size={18} /></button>
        </div>
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[22rem_1fr]">
          <div className="p-5 space-y-3 text-sm overflow-y-auto border-r">
            {!vv && !fehler && <Loader2 className="animate-spin text-slate-400" />}
            {vv && (
              <>
                <div><span className="text-slate-500">Versandweg:</span> <strong data-testid="sst-vv-kanal">{vv.kanal_label}</strong></div>
                <div><span className="text-slate-500">Empfänger:</span> <strong data-testid="sst-vv-empfaenger">{vv.empfaenger_name}</strong>
                  {vv.empfaenger_ansprechpartner ? <span> (z. Hd. {vv.empfaenger_ansprechpartner})</span> : null}</div>
                {kanal === 'email' && <div><span className="text-slate-500">E-Mail:</span> <span data-testid="sst-vv-email">{vv.empfaenger_email}</span></div>}
                {kanal === 'fax' && <div><span className="text-slate-500">Fax:</span> <span data-testid="sst-vv-fax">{vv.empfaenger_fax}</span></div>}
                {kanal === 'post' && <div className="whitespace-pre-line"><span className="text-slate-500">Anschrift:</span> {vv.empfaenger_adresse}</div>}
                <div><span className="text-slate-500">Absender:</span> <span data-testid="sst-vv-absender">{vv.absender_name || '—'}</span></div>
                <div><span className="text-slate-500">Betreff:</span> <span data-testid="sst-vv-betreff">{vv.betreff}</span></div>
                <div><span className="text-slate-500">PDF:</span> {vv.pdf_dateiname}{technisch ? ' (als Anhang)' : ''}</div>
                {(vv.anlagen || []).length > 0 && (
                  <div data-testid="sst-vv-anlagen">
                    <span className="text-slate-500">Anlagen{kanal === 'email' ? ' (werden angehängt)' : kanal === 'post' ? ' (bitte beilegen)' : ''}:</span>
                    <ul className="mt-0.5 space-y-0.5">
                      {vv.anlagen.map((x) => (
                        <li key={x.id} className={`flex items-center gap-1.5 ${x.verfuegbar ? '' : 'text-red-600'}`} data-testid="sst-vv-anlage">
                          {x.verfuegbar ? <Paperclip size={12} className="text-slate-400" /> : <AlertTriangle size={12} />}
                          <span className="truncate">{x.titel}</span>{!x.verfuegbar && <span className="text-xs">(Datei fehlt)</span>}
                        </li>))}
                    </ul>
                  </div>)}
                <div className="text-xs text-slate-400">Version {vv.version}</div>
                {vv.maengel.length > 0 && <div className="text-red-600" data-testid="sst-vv-maengel">{vv.maengel.join(' ')}</div>}
                {gesperrt && <div className="text-amber-700 bg-amber-50 rounded p-2" data-testid="sst-vv-gesperrt">Der {kanal === 'fax' ? 'Fax' : 'E-Mail'}-Versand ist in dieser Umgebung nicht freigegeben.</div>}
                <label className="flex items-start gap-2 pt-2">
                  <input type="checkbox" checked={bestaetigt} onChange={(e) => setBestaetigt(e.target.checked)} data-testid="sst-vv-bestaetigung" className="mt-1" />
                  <span>{(KANAL_TEXT[kanal] || KANAL_TEXT.post).bestaetigung}</span>
                </label>
              </>
            )}
            {fehler && <div className="text-red-600" data-testid="sst-vv-fehler">{fehler}</div>}
          </div>
          <div className="bg-slate-100 min-h-[60vh]">
            {pdfUrl ? <iframe title="Finale PDF" src={pdfUrl} className="w-full h-full min-h-[60vh]" data-testid="sst-vv-pdf" /> : <div className="p-6 text-slate-400">PDF wird erzeugt …</div>}
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onAbbrechen}>Abbrechen</Button>
          <Button onClick={ausfuehren} disabled={!vv || !bestaetigt || laeuft || vv.maengel.length > 0 || gesperrt} data-testid="sst-vv-ausfuehren">
            {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : kanal === 'email' ? <><Mail className="w-4 h-4 mr-1" /> {KANAL_TEXT.email.knopf}</>
              : kanal === 'fax' ? <><Printer className="w-4 h-4 mr-1" /> {KANAL_TEXT.fax.knopf}</>
                : <><CheckCircle2 className="w-4 h-4 mr-1" /> {KANAL_TEXT.post.knopf}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Neue Aufgabe aus der Korrespondenz (optional Unteraufgabe). Eine Kennung je geöffnetem Dialog: Doppelklick/Retry ergibt dieselbe Aufgabe. */
function AufgabeDialog({ entwurf, kontext, vorSpeichern, onAbbrechen, onAngelegt }) {
  const [token] = useState(() => neuerToken());
  const [titel, setTitel] = useState(entwurf.betreff ? `Rückmeldung: ${entwurf.betreff}` : '');
  const [beschreibung, setBeschreibung] = useState('');
  const [faellig, setFaellig] = useState('');
  const [eltern, setEltern] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  const haupt = hauptaufgaben(kontext ? kontext.aufgaben : []);
  const anlegen = async () => {
    if (laeuft) return;
    if (!titel.trim()) { setFehler('Bitte einen Titel eingeben.'); return; }
    setLaeuft(true); setFehler(null);
    try {
      const b = await vorSpeichern();                    // Aufgabe gehoert zu einem gespeicherten Schreiben
      if (!b) { setLaeuft(false); return; }
      const r = await schreibstubeAPI.aufgabeAnlegen(b.id, { titel: titel.trim(), beschreibung, faellig_am: faellig || null, eltern_aufgabe_id: eltern || null, client_token: token });
      onAngelegt(r);
    } catch (e) { setFehler(e.message); setLaeuft(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="sst-aufgabe-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-3 text-sm">
        <div className="flex items-center justify-between"><h2 className="font-semibold flex items-center gap-1.5"><ClipboardList size={16} /> Neue Aufgabe erstellen</h2>
          <button type="button" onClick={onAbbrechen} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
        <div className="text-xs text-slate-500">Für {kontext ? kontext.klient.name : ''} · verknüpft mit diesem Schreiben{entwurf.id ? '' : ' (wird dafür zuerst gespeichert)'}.</div>
        <Feld label="Titel" id="sst-aufgabe-titel" fehler={!titel.trim() && fehler ? fehler : null}>
          <Input id="sst-aufgabe-titel" value={titel} onChange={(e) => setTitel(e.target.value)} className="h-9" data-testid="sst-aufgabe-titel" autoFocus />
        </Feld>
        <Feld label="Beschreibung (optional)" id="sst-aufgabe-beschreibung">
          <Textarea id="sst-aufgabe-beschreibung" rows={3} value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} data-testid="sst-aufgabe-beschreibung" />
        </Feld>
        <div className="grid grid-cols-2 gap-2">
          <Feld label="Fällig am (optional)" id="sst-aufgabe-faellig">
            <Input id="sst-aufgabe-faellig" type="date" value={faellig} onChange={(e) => setFaellig(e.target.value)} className="h-9" data-testid="sst-aufgabe-faellig" />
          </Feld>
          <Feld label="Unteraufgabe von (optional)" id="sst-aufgabe-eltern">
            <select id="sst-aufgabe-eltern" className={SEL} value={eltern} onChange={(e) => setEltern(e.target.value)} data-testid="sst-aufgabe-eltern">
              <option value="">— eigenständige Aufgabe —</option>
              {haupt.map((x) => <option key={x.id} value={x.id}>{x.titel}</option>)}
            </select>
          </Feld>
        </div>
        {fehler && titel.trim() && <div className="text-red-600" data-testid="sst-aufgabe-fehler">{fehler}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onAbbrechen}>Abbrechen</Button>
          <Button onClick={anlegen} disabled={laeuft} data-testid="sst-aufgabe-anlegen">{laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aufgabe anlegen'}</Button>
        </div>
      </div>
    </div>
  );
}

/** Markierten Text als eigenen Textbaustein speichern (bestehende Vorlagenverwaltung, Kategorie „textbaustein“). */
function BausteinSpeichernDialog({ text, onAbbrechen, onGespeichert }) {
  const [name, setName] = useState(text.split('\n')[0].slice(0, 60));
  const [inhalt, setInhalt] = useState(text);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  const speichern = async () => {
    if (laeuft) return;
    if (!name.trim() || !inhalt.trim()) { setFehler('Bitte Namen und Text angeben.'); return; }
    setLaeuft(true); setFehler(null);
    try {
      const r = await schreibstubeAPI.bausteinSpeichern({ name: name.trim(), inhalt });
      if (r && r.success === false) throw new Error(r.error || 'Textbaustein nicht gespeichert');
      onGespeichert(r.vorlage);
    } catch (e) { setFehler(e.message); setLaeuft(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="sst-baustein-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-3 text-sm">
        <div className="flex items-center justify-between"><h2 className="font-semibold">Als Textbaustein speichern</h2>
          <button type="button" onClick={onAbbrechen} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
        <Feld label="Name des Textbausteins" id="sst-baustein-name">
          <Input id="sst-baustein-name" value={name} onChange={(e) => setName(e.target.value)} className="h-9" data-testid="sst-baustein-name" autoFocus />
        </Feld>
        <Feld label="Text" id="sst-baustein-text" hinweis="Platzhalter wie {{KLIENT_NAME}} werden beim Einfügen aus der Akte gefüllt.">
          <Textarea id="sst-baustein-text" rows={6} value={inhalt} onChange={(e) => setInhalt(e.target.value)} data-testid="sst-baustein-text" />
        </Feld>
        {fehler && <div className="text-red-600" data-testid="sst-baustein-fehler">{fehler}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onAbbrechen}>Abbrechen</Button>
          <Button onClick={speichern} disabled={laeuft} data-testid="sst-baustein-speichern">{laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}</Button>
        </div>
      </div>
    </div>
  );
}

/** Eigene Textbausteine suchen, bearbeiten und einfügen. */
function BausteineDialog({ liste, darfEinfuegen, onAbbrechen, onEinfuegen, onGeaendert }) {
  const [q, setQ] = useState('');
  const [bearbeite, setBearbeite] = useState(null);       // {id, name, inhalt}
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  const treffer = filtereBausteine(liste, q);
  const speichern = async () => {
    if (laeuft) return;
    if (!bearbeite.name.trim() || !bearbeite.inhalt.trim()) { setFehler('Bitte Namen und Text angeben.'); return; }
    setLaeuft(true); setFehler(null);
    try {
      const r = await schreibstubeAPI.bausteinAendern(bearbeite.id, { name: bearbeite.name.trim(), inhalt: bearbeite.inhalt });
      if (r && r.success === false) throw new Error(r.error || 'Nicht gespeichert');
      onGeaendert(r.vorlage);
      setBearbeite(null);
      toast.success('Textbaustein gespeichert');
    } catch (e) { setFehler(e.message); }
    setLaeuft(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="sst-bausteine-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col text-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b"><h2 className="font-semibold flex items-center gap-1.5"><Library size={16} /> Eigene Textbausteine</h2>
          <button type="button" onClick={onAbbrechen} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <Input placeholder="Suchen in Name und Text …" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" data-testid="sst-bausteine-suche" />
          {liste.length === 0 && <div className="text-slate-500">Noch keine eigenen Textbausteine. Text im Editor markieren und „Als Textbaustein speichern“ wählen.</div>}
          {liste.length > 0 && treffer.length === 0 && <div className="text-slate-500">Kein Textbaustein gefunden.</div>}
          {treffer.map((b) => (bearbeite && bearbeite.id === b.id ? (
            <div key={b.id} className="border rounded p-3 space-y-2 bg-violet-50/40" data-testid="sst-baustein-bearbeiten">
              <Input value={bearbeite.name} onChange={(e) => setBearbeite({ ...bearbeite, name: e.target.value })} className="h-8" data-testid="sst-baustein-bearbeiten-name" />
              <Textarea rows={5} value={bearbeite.inhalt} onChange={(e) => setBearbeite({ ...bearbeite, inhalt: e.target.value })} data-testid="sst-baustein-bearbeiten-text" />
              {fehler && <div className="text-red-600">{fehler}</div>}
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => { setBearbeite(null); setFehler(null); }}>Abbrechen</Button>
                <Button size="sm" onClick={speichern} disabled={laeuft} data-testid="sst-baustein-bearbeiten-speichern">{laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}</Button>
              </div>
            </div>
          ) : (
            <div key={b.id} className="border rounded p-3" data-testid="sst-baustein-eintrag">
              <div className="flex items-center gap-2">
                <span className="font-medium truncate">{b.name}</span>
                <div className="ml-auto flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setBearbeite({ id: b.id, name: b.name || '', inhalt: b.inhalt || '' })}
                    data-testid="sst-baustein-bearbeiten-knopf">Bearbeiten</Button>
                  <Button size="sm" className="h-7 text-xs" disabled={!darfEinfuegen} onClick={() => onEinfuegen(b)} data-testid="sst-baustein-einfuegen">Einfügen</Button>
                </div>
              </div>
              <div className="text-xs text-slate-600 whitespace-pre-line line-clamp-3 mt-1">{b.inhalt}</div>
            </div>
          )))}
        </div>
      </div>
    </div>
  );
}

/** Word-/PDF-Export des GESPEICHERTEN Stands (gleiche Komposition wie Versand). */
function ExportKnoepfe({ id, betreff }) {
  const lade = async (art) => {
    try {
      const blob = art === 'docx' ? await schreibstubeAPI.entwurfDocx(id) : await schreibstubeAPI.entwurfPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(betreff || 'Schreiben').replace(/[^A-Za-z0-9ÄÖÜäöüß_-]+/g, '_').slice(0, 60)}.${art}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => lade('docx')} data-testid="sst-export-docx"><Download className="w-4 h-4 mr-1" /> Word</Button>
      <Button variant="outline" size="sm" onClick={() => lade('pdf')} data-testid="sst-export-pdf"><Download className="w-4 h-4 mr-1" /> PDF</Button>
    </>
  );
}

export default function SchreibstubeArbeitsplatz() {
  const [searchParams, setSearchParams] = useTabSearchParams();
  const [entwurf, setEntwurf] = useState(() => leererEntwurf(searchParams.get('klient') || '', searchParams.get('aufgabe')));
  const [gespeichert, setGespeichert] = useState(null);
  const [kontext, setKontext] = useState(null);
  const [liste, setListe] = useState([]);
  const [vorschauUrl, setVorschauUrl] = useState(null);
  const [vorschauFehler, setVorschauFehler] = useState(null);
  const [speichert, setSpeichert] = useState(false);
  const [versandOffen, setVersandOffen] = useState(false);
  const [kontaktOffen, setKontaktOffen] = useState(false);
  const [zeoOffen, setZeoOffen] = useState(false);
  const [bausteine, setBausteine] = useState({ textbausteine: [], platzhalter: [] });
  const [log, setLog] = useState([]);
  const [versandwege, setVersandwege] = useState(null);      // was hier tatsaechlich funktioniert (Server)
  const [anlagenOpt, setAnlagenOpt] = useState([]);         // Dokumente der Akte dieses Klienten
  const [altbestand, setAltbestand] = useState([]);         // fruehere Ablagen, nur lesend
  const [eigeneBausteine, setEigeneBausteine] = useState([]);   // Kategorie „textbaustein“ der bestehenden Vorlagenverwaltung
  const [auswahl, setAuswahl] = useState(null);              // markierter Bereich im Textkoerper [start, ende]
  const [aufgabeOffen, setAufgabeOffen] = useState(false);
  const [bausteinText, setBausteinText] = useState(null);    // Dialog „Als Textbaustein speichern“
  const [bausteineOffen, setBausteineOffen] = useState(false);
  const [laedtHoch, setLaedtHoch] = useState(false);
  const dateiRef = useRef(null);
  const [spalten, setSpalten] = useState(() => ({ liste: 220, vorschau: 480, listeZu: false, ...leseSpalten() }));
  const zugStart = useRef(null);
  const geladen = useRef('');
  const textRef = useRef(null);
  const cursor = useRef(null);            // letzte Cursorposition im Textkoerper; null = noch nie gesetzt -> Einfuegen am Textende

  const versendet = entwurf.status && entwurf.status !== 'entwurf';
  const klientId = entwurf.klient_id;

  const ladeListe = useCallback((kid) => {
    if (!kid) { setListe([]); setAltbestand([]); setAnlagenOpt([]); return; }
    schreibstubeAPI.entwuerfe(kid).then((r) => setListe(r.entwuerfe || [])).catch(() => setListe([]));
    schreibstubeAPI.altbestand(kid).then((r) => setAltbestand(r.altbestand || [])).catch(() => setAltbestand([]));
    schreibstubeAPI.anlagen(kid).then((r) => setAnlagenOpt(r.anlagen || [])).catch(() => setAnlagenOpt([]));
  }, []);

  const ladeKontext = useCallback((kid) => schreibstubeAPI.kontext(kid).then((k) => { setKontext(k); return k; }).catch((e) => { toast.error(e.message); return null; }), []);

  useEffect(() => {
    if (!klientId) { setKontext(null); return; }
    ladeKontext(klientId);
    ladeListe(klientId);
  }, [klientId, ladeListe, ladeKontext]);

  useEffect(() => { schreibstubeAPI.textbausteine().then(setBausteine).catch(() => {}); }, []);
  const ladeEigeneBausteine = useCallback(() => schreibstubeAPI.eigeneBausteine().then((r) => setEigeneBausteine(r.vorlagen || [])).catch(() => setEigeneBausteine([])), []);
  useEffect(() => { ladeEigeneBausteine(); }, [ladeEigeneBausteine]);
  useEffect(() => { schreibstubeAPI.versandwege().then(setVersandwege).catch(() => setVersandwege(null)); }, []);

  // Einstieg mit Kontext ohne gespeichertes Schreiben: ?klient=<id>[&aufgabe=<id>] (Akte/Aufgabe "Brief erstellen") -> neues Schreiben
  useEffect(() => {
    const k = searchParams.get('klient');
    const a = searchParams.get('aufgabe') || null;
    if (searchParams.get('brief') || searchParams.get('zeo_vorschlag') || !k || entwurf.id) return;
    if (k === entwurf.klient_id && a === (entwurf.aufgabe_id || null)) return;
    if (k !== entwurf.klient_id) { setGespeichert(null); setLog([]); setKontext(null); }
    setEntwurf((e) => (e.id ? e : { ...leererEntwurf(k, a), ...(k === e.klient_id ? { ...e, aufgabe_id: a } : {}) }));
  }, [searchParams, entwurf.id, entwurf.klient_id, entwurf.aufgabe_id]);

  // ABSENDER: Standard = zustaendiger Betreuer (Server-Vorschlag), nur wenn noch keiner gewaehlt ist
  useEffect(() => {
    if (!kontext || versendet || entwurf.absender_user_id || !kontext.absender?.vorschlag || kontext.klient.id !== entwurf.klient_id) return;
    setEntwurf((e) => (e.absender_user_id ? e : { ...e, absender_user_id: kontext.absender.vorschlag }));
  }, [kontext, entwurf.absender_user_id, entwurf.klient_id, versendet]);

  // ANREDE: automatische Anrede folgt dem Empfaenger -- manuell geaenderte Anrede und aeltere Entwuerfe bleiben unberuehrt
  useEffect(() => {
    if (versendet) return;
    setEntwurf((e) => mitAutoAnrede(e, kontext && kontext.klient.id === e.klient_id ? kontext.empfaenger : null));
  }, [kontext, entwurf.empfaenger_ref, entwurf.empfaenger_name, entwurf.empfaenger_ansprechpartner, entwurf.anrede_manuell, entwurf.anrede, versendet]);

  // neues/anderes Schreiben: Cursorposition vergessen
  useEffect(() => { cursor.current = null; }, [entwurf.id, entwurf.client_token]);

  const uebernimmGespeichert = useCallback((b) => {
    setEntwurf({ ...b });
    setGespeichert(b);
    if (b.status !== 'entwurf') schreibstubeAPI.versandlog(b.id).then((r) => setLog(r.eintraege || [])).catch(() => setLog([]));
    else setLog([]);
  }, []);

  // Einstieg: ?brief=<id> (gespeichertes Schreiben) oder ?zeo_vorschlag=<message_id> (ZEO "In Schreibstube oeffnen"), optional &aktion=versenden
  useEffect(() => {
    const brief = searchParams.get('brief');
    const zeo = searchParams.get('zeo_vorschlag');
    const schluessel = `${brief || ''}|${zeo || ''}`;
    if (schluessel === '|' || schluessel === geladen.current) return;
    geladen.current = schluessel;
    if (brief) {
      schreibstubeAPI.entwurf(brief).then((b) => {
        uebernimmGespeichert(b);
        if (searchParams.get('aktion') === 'versenden' && b.status === 'entwurf') setVersandOffen(true);
      }).catch((e) => toast.error(e.message));
    } else if (zeo) {
      schreibstubeAPI.zeoVorschlag(zeo).then((v) => {
        if (v.brief_id) return schreibstubeAPI.entwurf(v.brief_id).then(uebernimmGespeichert);
        setGespeichert(null);
        setEntwurf(ausZeoVorschlag(v.entwurf, v.message_id));
        return null;
      }).catch((e) => toast.error(e.message));
    }
  }, [searchParams, uebernimmGespeichert]);

  // Live-Vorschau: rendert den aktuellen Stand, OHNE zu speichern (kein neuer Datensatz)
  const schluessel = useMemo(() => vorschauSchluessel(entwurf), [entwurf]);
  useEffect(() => {
    if (!entwurf.klient_id) { setVorschauUrl(null); return undefined; }
    let aktiv = true;
    let url = null;
    const t = setTimeout(() => {
      schreibstubeAPI.vorschau(entwurfBody(entwurf)).then((b) => {
        if (!aktiv) return;
        url = URL.createObjectURL(b);
        setVorschauUrl((alt) => { if (alt) URL.revokeObjectURL(alt); return url; });
        setVorschauFehler(null);
      }).catch((e) => { if (aktiv) setVorschauFehler(e.message); });
    }, 700);
    return () => { aktiv = false; clearTimeout(t); };
  }, [schluessel]); // eslint-disable-line react-hooks/exhaustive-deps

  const setze = (feld, wert) => setEntwurf((e) => ({ ...e, [feld]: wert }));
  const geaendert = istGeaendert(entwurf, gespeichert);
  const zustand = speicherZustand(entwurf, gespeichert, speichert);

  const waehleKlient = (k) => {
    if (k.id === klientId && !entwurf.id) return;
    if (entwurf.id && geaendert && !versendet && !window.confirm('Ungespeicherte Änderungen verwerfen und ein neues Schreiben für den anderen Klienten beginnen?')) return;
    setGespeichert(null);
    setLog([]);
    setKontext(null);
    setEntwurf(leererEntwurf(k.id));
    geladen.current = '';
    setSearchParams({ klient: k.id });
  };

  // Altbestand -> Korrespondenz: alter Brief = DERSELBE Datensatz; alter Korrespondenz-/Vorlagen-Entwurf = neuer Entwurf mit Rueckverweis
  const altUebernehmen = async (x) => {
    if (entwurf.id && geaendert && !versendet && !window.confirm('Ungespeicherte Änderungen verwerfen?')) return;
    try {
      const b = x.herkunft === 'briefe' ? await schreibstubeAPI.uebernehmen(x.id) : await schreibstubeAPI.altUebernehmen(x.id);
      (b.uebernahme_hinweise || []).forEach((h) => toast.info(h));
      geladen.current = `${b.id}|`;
      setSearchParams({ brief: b.id });
      uebernimmGespeichert(b);
      ladeListe(b.klient_id);
    } catch (e) { toast.error(e.message); }
  };

  const alsVorlage = async () => {
    const name = window.prompt('Name der Vorlage:', entwurf.betreff || '');
    if (!name || !name.trim()) return;
    try {
      const r = await schreibstubeAPI.vorlageSpeichern({ name: name.trim(), beschreibung: 'Aus der Korrespondenz gespeichert', kategorie: 'custom', inhalt: entwurf.inhalt || '' });
      if (r && r.success === false) throw new Error(r.error || 'Vorlage nicht gespeichert');
      toast.success('Als Vorlage gespeichert');
    } catch (e) { toast.error(e.message); }
  };

  const neuesSchreiben = () => {
    setGespeichert(null);
    setLog([]);
    setEntwurf(leererEntwurf(klientId));
    geladen.current = '';
    setSearchParams(klientId ? { klient: klientId } : {});
  };

  const speichern = async () => {
    if (speichert || versendet) return null;
    setSpeichert(true);
    try {
      const b = entwurf.id
        ? await schreibstubeAPI.speichern(entwurf.id, { ...entwurfBody(entwurf), version: entwurf.version })
        : await schreibstubeAPI.anlegen({ ...entwurfBody(entwurf), client_token: entwurf.client_token, zeo_message_id: entwurf.zeo_message_id || null });
      uebernimmGespeichert(b);
      geladen.current = `${b.id}|`;
      setSearchParams({ brief: b.id });
      ladeListe(b.klient_id);
      toast.success('Entwurf gespeichert');
      return b;
    } catch (e) {
      toast.error(e.message);
      return null;
    } finally {
      setSpeichert(false);
    }
  };

  const versendenOeffnen = async () => {
    const m = versandMaengel(entwurf);
    if (m.length) { toast.error(m.join(' ')); return; }
    const b = istGeaendert(entwurf, gespeichert) || !entwurf.id ? await speichern() : entwurf;
    if (b) setVersandOffen(true);
  };

  // TEXT: Textbaustein an der Cursorposition einfuegen -- Platzhalter nur aus kanonischen Daten (Server)
  const platzhalterKontext = () => ({ klient_id: klientId, absender_user_id: entwurf.absender_user_id, empfaenger_name: entwurf.empfaenger_name || '',
    bezug: entwurf.bezug, datum: entwurf.datum });
  // immer in den TEXTKOERPER: an der zuletzt gesetzten Cursorposition, sonst am Textende (nie vor die Anrede -- die steht nicht im Textfeld)
  const einfuegen = (einschub) => {
    const el = textRef.current;
    const pos = cursor.current;
    const { text, cursor: neu } = fuegeEin(entwurf.inhalt, pos ? pos[0] : null, pos ? pos[1] : null, einschub);
    setze('inhalt', text);
    cursor.current = [neu, neu];
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(neu, neu); } }, 0);
  };
  const merkeCursor = (ev) => {
    cursor.current = [ev.target.selectionStart, ev.target.selectionEnd];
    setAuswahl(ev.target.selectionEnd > ev.target.selectionStart ? [ev.target.selectionStart, ev.target.selectionEnd] : null);
  };
  const fuegeBausteinEin = async (key) => {
    const eigen = key.startsWith('eigen:') ? eigeneBausteine.find((x) => `eigen:${x.id}` === key) : null;
    const b = eigen ? { text: eigen.inhalt || '' } : bausteine.textbausteine.find((x) => x.key === key);
    if (!b || !klientId) return;
    try {
      const r = await schreibstubeAPI.platzhalter({ ...platzhalterKontext(), text: b.text });
      einfuegen(r.text);
      if (r.offen.length) toast.warning(`In der Akte fehlt: ${r.offen.join(', ')} – bitte im Text ergänzen.`);
    } catch (e) { toast.error(e.message); }
  };
  const fuegePlatzhalterEin = (key) => einfuegen(`{{${key}}}`);
  const markiert = markierterText(entwurf.inhalt, auswahl);

  // ANLAGE VOM PC: bestehende Dokumentablage der Akte (Server prueft Typ, Inhalt, Groesse, Klient), danach als Anlage gewaehlt
  const anlageHochladen = async (datei) => {
    if (!datei || laedtHoch || !klientId) return;
    const fehler = anlageVorpruefung(datei);
    if (fehler) { toast.error(fehler); return; }
    setLaedtHoch(true);
    try {
      const d = await schreibstubeAPI.anlageHochladen(klientId, datei);
      setEntwurf((x) => mitAnlage(x, d));
      setAnlagenOpt((alt) => [d, ...alt.filter((x) => x.id !== d.id)]);
      toast.success(`„${d.titel}“ in der Akte gespeichert und angehängt – Entwurf speichern nicht vergessen.`);
    } catch (e) { toast.error(e.message); }
    setLaedtHoch(false);
    if (dateiRef.current) dateiRef.current.value = '';
  };

  // AUFGABE: Schreiben vorher speichern (die Aufgabe gehoert zu einem gespeicherten Entwurf)
  const vorAufgabeSpeichern = async () => (istGeaendert(entwurf, gespeichert) || !entwurf.id ? speichern() : entwurf);
  const aufgabeAngelegt = async (r) => {
    setAufgabeOffen(false);
    uebernimmGespeichert(r.brief);
    await ladeKontext(r.brief.klient_id);
    if (r.hinweis) toast.info(r.hinweis);
    toast.success(r.bereits_angelegt ? 'Aufgabe war bereits angelegt – keine zweite erstellt.' : `Aufgabe „${r.aufgabe.titel}“ angelegt.`);
  };
  const setzePlatzhalter = async () => {
    try {
      const r = await schreibstubeAPI.platzhalter({ ...platzhalterKontext(), text: entwurf.inhalt || '', betreff: entwurf.betreff || '' });
      setEntwurf((e) => ({ ...e, inhalt: r.text, betreff: r.betreff }));
      if (r.offen.length) toast.warning(`In der Akte fehlt: ${r.offen.join(', ')} – bitte im Text ergänzen.`);
      else toast.success('Platzhalter eingesetzt.');
    } catch (e) { toast.error(e.message); }
  };

  // Spalten: Breite ziehen, merken (nur Komfort, lokal)
  const zieheListe = (dx) => setSpalten((s) => ({ ...s, liste: begrenze(zugStart.current.liste + dx, 180, 420) }));
  const zieheVorschau = (dx) => setSpalten((s) => ({ ...s, vorschau: begrenze(zugStart.current.vorschau - dx, 320, 1100) }));
  const zugAnfang = () => { zugStart.current = spalten; };
  const zugEnde = () => setSpalten((s) => { merkeSpalten(s); return s; });
  const listeUmschalten = () => setSpalten((s) => { const n = { ...s, listeZu: !s.listeZu }; merkeSpalten(n); return n; });

  const empfaenger = kontext ? kontext.empfaenger : [];
  const sachgebiete = kontext ? kontext.sachgebiete : [];
  const absender = kontext ? kontext.absender : null;
  const bezugOpt = bezugOptionen(sachgebiete, entwurf.sachgebiet);
  const offen = offenePlatzhalter(entwurf.betreff, entwurf.anrede, entwurf.inhalt);
  const aufbau = aufbauMaengel(entwurf);
  const altform = entwurf.anrede === null || entwurf.anrede === undefined;
  const anredeVorschlag = altform ? null : autoAnrede(entwurf, kontext ? kontext.empfaenger : null);
  const gewaehlterAbsender = absender && absender.optionen.find((o) => o.user_id === entwurf.absender_user_id);

  return (
    <div className="h-full flex flex-col lg:flex-row gap-0 p-3 bg-slate-50 min-h-[calc(100vh-6rem)]" data-testid="schreibstube-seite">
      {/* Spalte 1: Schreiben des Klienten */}
      {spalten.listeZu ? (
        <button type="button" onClick={listeUmschalten} className="hidden lg:flex w-7 shrink-0 bg-white border rounded-lg items-start justify-center pt-3 text-slate-500 hover:text-violet-700"
          title="Liste einblenden" data-testid="sst-liste-auf"><ChevronRight size={16} /></button>
      ) : (
        <aside className="lg:shrink-0 lg:w-[var(--sst-liste)] bg-white rounded-lg border flex flex-col min-h-0" style={{ '--sst-liste': `${spalten.liste}px` }} data-testid="sst-liste">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50 rounded-t-lg">
            <h2 className="font-semibold text-slate-700 text-sm flex items-center gap-1.5"><FileText size={15} /> Schreiben</h2>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={neuesSchreiben} data-testid="sst-neu" title="Neues Schreiben"><Plus size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={listeUmschalten} className="hidden lg:inline-flex" title="Liste ausblenden" data-testid="sst-liste-zu"><ChevronLeft size={14} /></Button>
            </div>
          </div>
          <div className="p-2 space-y-1 overflow-y-auto">
            {!klientId && <p className="text-xs text-slate-400 px-1">Klient wählen, um Schreiben zu sehen.</p>}
            {liste.map((b) => (
              <button key={b.id} type="button" onClick={() => { geladen.current = `${b.id}|`; setSearchParams({ brief: b.id }); uebernimmGespeichert(b); }}
                className={`block w-full text-left rounded px-2 py-1.5 text-sm hover:bg-violet-50 ${b.id === entwurf.id ? 'bg-violet-50 ring-1 ring-violet-200' : ''}`}
                data-testid="sst-liste-eintrag">
                <div className="truncate font-medium">{b.betreff || '(ohne Betreff)'}</div>
                <div className="text-xs text-slate-500 truncate">{b.empfaenger_name || '—'} · {STATUS_LABEL[b.status] || b.status}</div>
              </button>
            ))}
            {altbestand.length > 0 && (
              <div className="pt-2 mt-2 border-t" data-testid="sst-altbestand">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 px-1 flex items-center gap-1"><History size={12} /> Früher erstellt (nur lesen)</div>
                {altbestand.map((x) => (
                  <div key={`${x.herkunft}:${x.id}`} className="px-2 py-1 text-xs text-slate-600" data-testid="sst-alt-eintrag">
                    <div className="truncate">{x.betreff}</div>
                    <div className="text-slate-400 truncate">{x.empfaenger || '—'} · {x.status || '—'}{x.uebernommen_in ? ' · übernommen' : ''}</div>
                    {x.uebernehmbar && (
                      <button type="button" className="text-violet-700 hover:underline" onClick={() => altUebernehmen(x)} data-testid="sst-alt-uebernehmen">
                        Hier weiterbearbeiten
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      )}
      {!spalten.listeZu && <Ziehgriff onStart={zugAnfang} onZiehen={zieheListe} onEnde={zugEnde} testid="sst-griff-liste" />}

      {/* Spalte 2: Formular (Reihenfolge verbindlich) + grosser Editor unten */}
      <section className="flex-1 min-w-0 lg:min-w-[34rem] bg-white rounded-lg border flex flex-col my-3 lg:my-0" data-testid="sst-formular">
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-slate-50 rounded-t-lg">
          <Badge className={versendet ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'} data-testid="sst-status">{STATUS_LABEL[entwurf.status] || entwurf.status}</Badge>
          {entwurf.version ? <span className="text-xs text-slate-400" data-testid="sst-version">Version {entwurf.version}</span> : null}
          {zustand.text && (
            <span data-testid="sst-speicherzustand" data-art={zustand.art}
              className={`text-xs flex items-center gap-1 ${zustand.art === 'gespeichert' ? 'text-green-700' : zustand.art === 'laeuft' ? 'text-slate-500' : 'text-amber-700'}`}>
              {zustand.art === 'laeuft' ? <Loader2 size={12} className="animate-spin" /> : zustand.art === 'gespeichert' ? <CheckCircle2 size={12} /> : null}{zustand.text}
            </span>)}
          <span className="ml-auto text-xs text-slate-400" data-testid="sst-erfasst-von">{(gespeichert && gespeichert.erfasst_von) || `Erfasst von: ${kontext ? kontext.von.name : '—'}`}</span>
        </div>
        <div className="px-4 py-2 space-y-0 overflow-y-auto">
          {(entwurf.hinweise || []).length > 0 && (
            <div className="text-xs bg-violet-50 text-violet-800 rounded p-2 my-2 space-y-0.5" data-testid="sst-zeo-hinweise">
              {entwurf.hinweise.map((h) => <div key={h}>{h}</div>)}
            </div>
          )}
          <AufgabenBezug kontext={gespeichert && gespeichert.id === entwurf.id ? gespeichert.aufgabe_kontext : null} versendet={versendet}
            klientName={gespeichert && gespeichert.klient_name} />
          <PruefAnsicht pruefung={entwurf.pruefung} offen testid="sst-pruefansicht" />
          <Zeile label="Für" testid="sst-zeile-fuer">
            <KlientWahl klient={kontext ? kontext.klient : null} onWaehlen={waehleKlient}
              sperrHinweis={entwurf.id ? 'Ein anderer Klient beginnt ein neues Schreiben; dieses bleibt gespeichert.' : null} />
          </Zeile>
          <Zeile label="In der Sache" testid="sst-zeile-sache">
            <select className={SEL} disabled={!kontext || versendet} value={entwurf.sachgebiet || ''} data-testid="sst-sachgebiet"
              onChange={(e) => { const key = e.target.value || null; setEntwurf((alt) => mitSachgebiet(alt, key, kontext)); }}>
              <option value="">— Sachgebiet —</option>
              {sachgebiete.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </Zeile>
          <Zeile label="Bezüglich" testid="sst-zeile-bezug" hinweis={entwurf.sachgebiet && bezugOpt.length === 0 ? 'Keine Nummer in der Akte hinterlegt.' : null}>
            <select className={SEL} disabled={!bezugOpt.length || versendet} value={entwurf.bezug ? `${entwurf.bezug.quelle}|${entwurf.bezug.wert}` : ''} data-testid="sst-bezug"
              onChange={(e) => setze('bezug', bezugOpt.find((o) => `${o.quelle}|${o.wert}` === e.target.value) || null)}>
              <option value="">— kein Bezug —</option>
              {bezugOpt.map((o) => <option key={`${o.quelle}|${o.wert}`} value={`${o.quelle}|${o.wert}`}>{o.label}: {o.wert}{o.herkunft ? ` (${o.herkunft})` : ''}</option>)}
            </select>
          </Zeile>
          <Zeile label="An" testid="sst-zeile-an">
            <AnBlock key={`${entwurf.id || 'neu'}-${klientId}`} entwurf={entwurf} setEntwurf={setEntwurf} empfaenger={empfaenger} sachgebiete={sachgebiete}
              kontext={kontext} gesperrt={versendet || !kontext} onKontaktSpeichern={() => setKontaktOffen(true)} />
          </Zeile>
          <Zeile label="Versandweg" testid="sst-zeile-versandweg">
            <div className="flex flex-wrap gap-1.5" data-testid="sst-versandweg">
              {VERSANDWEGE.map((v) => {
                const st = versandwege && versandwege[v.key];
                return (
                  <button key={v.key} type="button" disabled={versendet} onClick={() => setze('versandweg', v.key)} data-testid={`sst-versandweg-${v.key}`}
                    title={st && st.hinweis ? st.hinweis : undefined}
                    className={`h-9 px-3 rounded-md border text-sm ${entwurf.versandweg === v.key ? 'bg-violet-600 text-white border-violet-600' : 'bg-white hover:bg-violet-50'}`}>
                    {v.label}{st && !st.verfuegbar ? ' – gesperrt' : st && st.modus === 'testsenke' ? ' (TEST)' : ''}
                  </button>
                );
              })}
              {SPAETERE_VERSANDWEGE.map((v) => (
                <button key={v.key} type="button" disabled className="h-9 px-3 rounded-md border text-sm text-slate-400 bg-slate-50 cursor-not-allowed"
                  title={(versandwege && versandwege.egvp && versandwege.egvp.hinweis) || 'Nur Simulation'}>{v.label}</button>))}
            </div>
            {versandwege && entwurf.versandweg && versandwege[entwurf.versandweg] && versandwege[entwurf.versandweg].hinweis && (
              <span className={`block text-xs mt-1 ${versandwege[entwurf.versandweg].verfuegbar ? 'text-slate-500' : 'text-red-700'}`} data-testid="sst-versandweg-status">
                {versandwege[entwurf.versandweg].hinweis}
              </span>
            )}
            {entwurf.versandweg === 'email' && !entwurf.empfaenger_email && <span className="block text-xs text-amber-700 mt-1">Bitte unter „An“ eine E-Mail-Adresse eintragen.</span>}
            {entwurf.versandweg === 'fax' && !entwurf.empfaenger_fax && <span className="block text-xs text-amber-700 mt-1">Bitte unter „An“ eine Faxnummer eintragen.</span>}
          </Zeile>
          <Zeile label="Anlagen" testid="sst-zeile-anlagen"
            hinweis={entwurf.versandweg === 'fax' && (entwurf.anhaenge || []).length ? 'Per Fax wird nur das Schreiben übertragen – Anlagen per E-Mail oder Brief.'
              : 'Dokumente aus der Akte dieses Klienten oder vom PC (PDF, JPG, PNG, TIFF, DOCX, XLSX, ODT, TXT; höchstens 15 MB) – hochgeladene Dateien werden in der Akte gespeichert.'}>
            <div className="space-y-1" data-testid="sst-anlagen">
              {(entwurf.anhaenge || []).map((a) => (
                <div key={a.id} className="flex items-center gap-2 text-sm" data-testid="sst-anlage">
                  <Paperclip size={13} className="text-slate-400" /><span className="truncate">{a.titel}</span>
                  {!versendet && <button type="button" className="ml-auto text-slate-400 hover:text-red-600" onClick={() => setEntwurf((e) => ohneAnlage(e, a.id))}
                    title="Anlage entfernen" data-testid="sst-anlage-entfernen"><X size={14} /></button>}
                </div>
              ))}
              {!versendet && (
                <select className={SEL} value="" disabled={!klientId || !anlagenOpt.length} data-testid="sst-anlage-waehlen"
                  onChange={(e) => { const d = anlagenOpt.find((x) => x.id === e.target.value); if (d) setEntwurf((x) => mitAnlage(x, d)); }}>
                  <option value="">{anlagenOpt.length ? '+ Dokument aus der Akte anhängen …' : 'Keine Dokumente in der Akte'}</option>
                  {anlagenOpt.filter((d) => !(entwurf.anhaenge || []).some((a) => a.id === d.id)).map((d) => (
                    <option key={d.id} value={d.id}>{d.titel}{d.datum ? ` (${d.datum})` : ''}</option>))}
                </select>
              )}
              {!versendet && (
                <div>
                  <input ref={dateiRef} type="file" className="hidden" accept={ANLAGE_ENDUNGEN.join(',')} data-testid="sst-anlage-datei"
                    onChange={(e) => anlageHochladen(e.target.files && e.target.files[0])} />
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs" disabled={!klientId || laedtHoch}
                    onClick={() => dateiRef.current && dateiRef.current.click()} data-testid="sst-anlage-hochladen">
                    {laedtHoch ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    {laedtHoch ? 'Wird hochgeladen …' : 'Datei vom PC anhängen …'}
                  </Button>
                </div>
              )}
            </div>
          </Zeile>
          <Zeile label="Absender" testid="sst-zeile-absender"
            hinweis={absender && absender.vorschlag && entwurf.absender_user_id === absender.vorschlag
              ? (absender.vorschlag_grund === 'zustaendiger_betreuer' ? 'Zuständiger Betreuer laut Akte' : 'Hinterlegter Unterzeichner des Büros')
              : absender && absender.zustaendiger_betreuer && !absender.vorschlag ? `Zuständiger Betreuer „${absender.zustaendiger_betreuer}“ ist kein aktiver Benutzer – bitte wählen.` : null}>
            <select className={SEL} disabled={!absender || versendet} value={entwurf.absender_user_id || ''} data-testid="sst-absender"
              onChange={(e) => setze('absender_user_id', e.target.value || null)}>
              <option value="">— Absender wählen —</option>
              {(absender ? absender.optionen : []).map((o) => <option key={o.user_id} value={o.user_id}>{o.name}</option>)}
            </select>
            {versendet && entwurf.absender_name && !gewaehlterAbsender && <span className="block text-sm mt-1">{entwurf.absender_name}</span>}
          </Zeile>
          <Zeile label="Aufgabe" testid="sst-zeile-aufgabe">
            <select className={SEL} disabled={!kontext || versendet} value={entwurf.aufgabe_id || ''} data-testid="sst-aufgabe" onChange={(e) => setze('aufgabe_id', e.target.value || null)}>
              <option value="">— keine Aufgabe —</option>
              {(kontext ? kontext.aufgaben : []).map((a) => <option key={a.id} value={a.id}>{a.eltern_aufgabe_id ? '↳ ' : ''}{a.titel}</option>)}
            </select>
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs mt-1.5" disabled={!kontext || speichert} onClick={() => setAufgabeOffen(true)}
              data-testid="sst-aufgabe-neu"><ClipboardList className="w-3.5 h-3.5 mr-1" /> Neue Aufgabe erstellen …</Button>
          </Zeile>
          <Zeile label="Vorgang" testid="sst-zeile-vorgang">
            <select className={SEL} disabled={!kontext || versendet} value={entwurf.vorgang_id || ''} data-testid="sst-vorgang" onChange={(e) => setze('vorgang_id', e.target.value || null)}>
              <option value="">— kein Vorgang —</option>
              {(kontext ? kontext.vorgaenge || [] : []).map((v) => <option key={v.id} value={v.id}>{v.bezeichnung}{v.institution ? ` (${v.institution})` : ''}</option>)}
            </select>
          </Zeile>
          <Zeile label="Ist Antwort auf" testid="sst-zeile-antwort">
            <select className={SEL} disabled={!kontext || versendet} value={entwurf.ist_antwort_auf || ''} data-testid="sst-antwort-auf" onChange={(e) => setze('ist_antwort_auf', e.target.value || null)}>
              <option value="">— (optional) —</option>
              {(kontext ? kontext.antwort_auf : []).map((d) => <option key={d.id} value={d.id}>{d.titel}{d.datum ? ` (${d.datum})` : ''}</option>)}
            </select>
          </Zeile>
          <Zeile label="Betreff" testid="sst-zeile-betreff">
            <Input value={entwurf.betreff || ''} disabled={versendet} onChange={(e) => setze('betreff', e.target.value)} className="h-9" data-testid="sst-betreff" />
          </Zeile>
        </div>

        {/* TEXT: gross, unten, mit Bausteinen / Platzhaltern / ZEO */}
        <div className="flex-1 flex flex-col border-t px-4 py-3 min-h-[48vh]" data-testid="sst-zeile-text">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mr-1">Text</span>
            <select className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" disabled={versendet || !klientId} value="" data-testid="sst-textbaustein"
              onChange={(e) => fuegeBausteinEin(e.target.value)}>
              <option value="">Textbaustein einfügen …</option>
              <optgroup label="Standard">
                {bausteine.textbausteine.map((b) => <option key={b.key} value={b.key}>{b.titel}</option>)}
              </optgroup>
              {eigeneBausteine.length > 0 && (
                <optgroup label="Eigene Textbausteine">
                  {eigeneBausteine.map((b) => <option key={b.id} value={`eigen:${b.id}`}>{b.name}</option>)}
                </optgroup>)}
            </select>
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={versendet || !markiert} onMouseDown={(e) => e.preventDefault()}
              onClick={() => setBausteinText(markiert)} data-testid="sst-baustein-aus-auswahl"
              title={markiert ? 'Markierten Text als eigenen Textbaustein speichern' : 'Zuerst Text im Editor markieren'}>
              <BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Als Textbaustein speichern</Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setBausteineOffen(true)} data-testid="sst-bausteine-verwalten">
              <Library className="w-3.5 h-3.5 mr-1" /> Eigene Textbausteine …</Button>
            <select className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" disabled={versendet} value="" data-testid="sst-platzhalter-einfuegen"
              onChange={(e) => e.target.value && fuegePlatzhalterEin(e.target.value)}>
              <option value="">Platzhalter …</option>
              {bausteine.platzhalter.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={versendet || !klientId} onClick={setzePlatzhalter} data-testid="sst-platzhalter-setzen">Platzhalter aus der Akte füllen</Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={versendet || !klientId} onClick={() => setZeoOffen(true)} data-testid="sst-zeo-formulieren">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Mit ZEO formulieren</Button>
          </div>
          {offen.length > 0 && <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mb-2" data-testid="sst-offene-platzhalter">Noch offen: {offen.join(', ')} – vor dem Versand ausfüllen.</div>}
          {/* Briefaufbau wie im PDF: ANREDE -> TEXT -> GRUSSFORMEL -> ABSENDER */}
          {altform ? (
            <div className="text-xs bg-slate-50 border rounded px-2 py-1.5 mb-2 flex flex-wrap items-center gap-2" data-testid="sst-anrede-altform">
              <span>Älterer Entwurf: Anrede und Grußformel stehen im Text selbst.</span>
              {!versendet && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEntwurf((e) => anredeAusTextLoesen(e, kontext ? kontext.empfaenger : null))}
                data-testid="sst-anrede-loesen">Anrede aus dem Text lösen</Button>}
            </div>
          ) : (
            <div className="mb-2" data-testid="sst-anrede-block">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-16 shrink-0">Anrede</span>
                <Input value={entwurf.anrede || ''} disabled={versendet} className="h-8 text-[14px]" data-testid="sst-anrede"
                  onChange={(e) => setEntwurf((x) => ({ ...x, anrede: e.target.value, anrede_manuell: true }))} />
                {entwurf.anrede_manuell && !versendet && (
                  <Button size="sm" variant="ghost" className="h-8 text-xs shrink-0" onClick={() => setEntwurf((x) => ({ ...x, anrede_manuell: false }))}
                    data-testid="sst-anrede-auto" title="Anrede wieder automatisch aus dem Empfänger bilden">Automatisch</Button>)}
              </div>
              <div className="text-[11px] text-slate-400 ml-[4.5rem] mt-0.5" data-testid="sst-anrede-info">
                {entwurf.anrede_manuell
                  ? (anredeVorschlag && anredeVorschlag !== entwurf.anrede ? `Manuell geändert – Vorschlag für den Empfänger: „${anredeVorschlag}“` : 'Manuell geändert')
                  : 'Automatisch aus dem Empfänger'}
              </div>
            </div>
          )}
          <Textarea ref={textRef} value={entwurf.inhalt || ''} disabled={versendet} onChange={(e) => { setze('inhalt', e.target.value); merkeCursor(e); }}
            onSelect={merkeCursor} data-testid="sst-inhalt" placeholder="Brieftext (ohne Anrede und Grußformel) …"
            className="flex-1 min-h-[40vh] resize-y text-[14px] leading-relaxed font-sans" spellCheck />
          {aufbau.length > 0 && <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1" data-testid="sst-aufbau-hinweis">{aufbau.join(' ')}</div>}
          <div className="text-[14px] text-slate-600 mt-2 pl-1 whitespace-pre-line" data-testid="sst-schluss">
            {GRUSSFORMEL}{'\n\n'}{gewaehlterAbsender ? [gewaehlterAbsender.name, gewaehlterAbsender.titel].filter(Boolean).join('\n') : (entwurf.absender_name || '— Absender wählen —')}
          </div>
          {!versendet ? (
            <div className="flex gap-2 justify-end pt-3">
              <Button variant="outline" onClick={speichern} disabled={!klientId || speichert} data-testid="sst-speichern">
                {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Als Entwurf speichern</>}
              </Button>
              {entwurf.id && <ExportKnoepfe id={entwurf.id} betreff={entwurf.betreff} />}
              <Button variant="outline" size="sm" disabled={!(entwurf.inhalt || '').trim()} onClick={alsVorlage} data-testid="sst-als-vorlage"
                title="Text als wiederverwendbare Vorlage speichern (Vorlagen-Bibliothek)"><BookmarkPlus className="w-4 h-4 mr-1" /> Als Vorlage</Button>
              <Button onClick={versendenOeffnen} disabled={!klientId || speichert} data-testid="sst-versenden"><Send className="w-4 h-4 mr-1" /> Versenden …</Button>
            </div>
          ) : (
            <div className="rounded border border-green-200 bg-green-50 p-3 text-sm space-y-1 mt-3" data-testid="sst-versand-info">
              <div><strong>Versendet</strong> ({entwurf.versand?.kanal === 'email' ? 'E-Mail' : entwurf.versand?.kanal === 'fax' ? 'Fax' : 'Brief/Post'}) am {entwurf.versanddatum} · Absender {entwurf.versand?.absender_name || entwurf.absender_name || '—'} · erfasst von {entwurf.versand?.von_name}</div>
              {entwurf.versand?.message_id && <div className="text-xs text-slate-500">Message-ID: {entwurf.versand.message_id}</div>}
              {entwurf.versand?.dokument_id && <div className="text-xs text-slate-500" data-testid="sst-akte">PDF in der Akte abgelegt.</div>}
              {(entwurf.versand?.anlagen || []).length > 0 && (
                <div className="text-xs text-slate-600" data-testid="sst-versand-anlagen">
                  Anlagen: {entwurf.versand.anlagen.map((x) => x.titel).join(', ')}{entwurf.versand.kanal === 'email' ? ' (angehängt)' : ''}
                </div>)}
              {entwurf.id && <div className="flex gap-2 pt-1"><ExportKnoepfe id={entwurf.id} betreff={entwurf.betreff} /></div>}
              {log.length > 0 && (
                <ul className="text-xs text-slate-600 pt-1" data-testid="sst-sendelog">
                  {log.map((l) => <li key={l.id}>{(l.zeit || '').slice(0, 16).replace('T', ' ')} · {l.ergebnis} · {l.kanal} · {l.actor_display_name_snapshot}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      </section>

      <Ziehgriff onStart={zugAnfang} onZiehen={zieheVorschau} onEnde={zugEnde} testid="sst-griff-vorschau" />
      {/* Spalte 3: Live-Vorschau */}
      <section className="min-w-0 lg:min-w-[18rem] lg:shrink lg:grow-0 lg:basis-[var(--sst-vorschau)] bg-white rounded-lg border flex flex-col min-h-[70vh]" style={{ '--sst-vorschau': `${spalten.vorschau}px` }} data-testid="sst-vorschau">
        <div className="px-3 py-2 border-b bg-slate-50 rounded-t-lg text-xs text-slate-500">Live-Vorschau (Briefkopf, nicht gespeichert)</div>
        {vorschauFehler && <div className="px-3 py-2 text-xs text-red-600" data-testid="sst-vorschau-fehler">{vorschauFehler}</div>}
        {vorschauUrl ? <iframe title="Live-Vorschau" src={vorschauUrl} className="flex-1 w-full" data-testid="sst-vorschau-pdf" />
          : <div className="flex-1 flex items-center justify-center text-sm text-slate-400">{klientId ? 'Vorschau wird erzeugt …' : 'Klient wählen'}</div>}
      </section>

      {versandOffen && entwurf.id && (
        <VersandDialog entwurf={entwurf} onAbbrechen={() => setVersandOffen(false)}
          onVersendet={(r) => { setVersandOffen(false); uebernimmGespeichert(r.brief); ladeListe(r.brief.klient_id); }} />
      )}
      {kontaktOffen && (
        <KontaktDialog entwurf={entwurf} onAbbrechen={() => setKontaktOffen(false)}
          onGespeichert={async (r) => {
            setKontaktOffen(false);
            const k = await ladeKontext(klientId);
            const e = (k ? k.empfaenger : []).find((x) => refSchluessel(x.ref) === refSchluessel(r.ref)) || r.empfaenger;
            if (e) setEntwurf((alt) => ({ ...mitEmpfaenger(alt, e, k ? k.sachgebiete : sachgebiete), empfaenger_ansprechpartner: alt.empfaenger_ansprechpartner,
              empfaenger_telefon: alt.empfaenger_telefon || e.telefon || '', empfaenger_fax: alt.empfaenger_fax || e.fax || '', empfaenger_email: alt.empfaenger_email || e.email || '' }));
            toast.success(r.neu_angelegt ? 'Kontakt angelegt und mit der Akte verknüpft.' : 'Kontakt mit der Akte verknüpft.');
          }} />
      )}
      {aufgabeOffen && (
        <AufgabeDialog entwurf={entwurf} kontext={kontext} vorSpeichern={vorAufgabeSpeichern} onAbbrechen={() => setAufgabeOffen(false)} onAngelegt={aufgabeAngelegt} />
      )}
      {bausteinText !== null && (
        <BausteinSpeichernDialog text={bausteinText} onAbbrechen={() => setBausteinText(null)}
          onGespeichert={(v) => { setBausteinText(null); setEigeneBausteine((alt) => [...alt.filter((x) => x.id !== v.id), v]); toast.success(`Textbaustein „${v.name}“ gespeichert.`); }} />
      )}
      {bausteineOffen && (
        <BausteineDialog liste={eigeneBausteine} darfEinfuegen={!versendet && !!klientId} onAbbrechen={() => setBausteineOffen(false)}
          onEinfuegen={(b) => { setBausteineOffen(false); fuegeBausteinEin(`eigen:${b.id}`); }}
          onGeaendert={(v) => setEigeneBausteine((alt) => alt.map((x) => (x.id === v.id ? v : x)))} />
      )}
      {zeoOffen && (
        <ZeoDialog entwurf={entwurf} kontext={kontext} onAbbrechen={() => setZeoOffen(false)}
          onUebernehmen={(text, modus) => {
            setZeoOffen(false);
            setEntwurf((e) => ({ ...e, inhalt: modus === 'ersetzen' ? text : `${(e.inhalt || '').trimEnd()}\n\n${text}`.replace(/^\n+/, '') }));
            toast.success('Vorschlag übernommen – bitte prüfen und speichern.');
          }} />
      )}
    </div>
  );
}
